"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { InstallPrompt } from "./InstallPrompt";

const PwaContext = createContext(null);

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.__IS_PWA !== undefined ? window.__IS_PWA : (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true);
}

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export function PwaProvider({ children }) {
  const [swReady, setSwReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        setSwReady(true);
        // Check for updates periodically
        reg.update();
        reg.onupdatefound = () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.onstatechange = () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New worker available, potentially reload or notify
                window.location.reload();
              }
            };
          }
        };
      })
      .catch(() => setSwReady(false));
  }, []);

  const value = useMemo(() => ({ swReady, ios: isIos(), mobile: isMobile(), standalone: isStandalone() }), [swReady]);

  return (
    <PwaContext.Provider value={value}>
      {children}
      <InstallPrompt />
    </PwaContext.Provider>
  );
}

export function usePwa() {
  const ctx = useContext(PwaContext);
  if (!ctx) throw new Error("usePwa must be used within PwaProvider");
  return ctx;
}

